import React, { memo, useMemo, useRef, useEffect, useCallback, useState } from "react";
import {
  GoogleMap,
  Circle,
  DirectionsRenderer,
  OverlayView,
} from "@react-google-maps/api";
import { alpha, useTheme } from "@mui/material/styles";
import PersonIcon from "@mui/icons-material/Person";
import { getAuth } from "firebase/auth";
import useUserProfileStore, {
  selectUserDisplayName,
  selectUserProfileImage,
} from "../../store/useUserProfileStore";
import { buildAvatarImageAttrs } from "../../utils/imageUrl";
import IconCluster from "../../assets/icons/v2/cluster.webp";

const MAP_FALLBACK_CENTER = { lat: 40.7128, lng: -74.006 };
const DEFAULT_MAP_ZOOM = 18;
const USER_AVATAR_MIN_VISIBLE_ZOOM = 10;
const MARKER_Z_INDEX = 100;
const CLUSTER_Z_INDEX = 10000;
const CLUSTER_CIRCLE_CENTER_Y_RATIO = 0.74;
const IOS_MAP_RECOVERY_TIMEOUTS_MS = [0, 120, 320];
const DEBUG_HOME_MAP_SHOVEL =
  String(
    process.env.REACT_APP_DEBUG_HOME_MAP_SHOVEL ||
      (typeof window !== "undefined"
        ? window.localStorage?.getItem("debugHomeMapShovel")
        : "") ||
      ""
  ).toLowerCase() === "true";
const debugSponsoredAvatar =
  String(process.env.REACT_APP_DEBUG_SPONSORED_AVATAR || "").toLowerCase() === "true";

function getClusterLabel(count, displayScale = 1) {
  const scale = Number.isFinite(Number(displayScale))
    ? Math.min(1.3, Math.max(0.65, Number(displayScale)))
    : 1;
  return {
    text: Number(count) >= 100 ? "99+" : String(count),
    color: "#FFFFFF",
    fontFamily: "Arial, sans-serif",
    fontSize: `${Math.round(14 * scale)}px`,
    fontWeight: "700",
  };
}

function isLatLngLiteral(value) {
  return (
    value &&
    typeof value === "object" &&
    Number.isFinite(value.lat) &&
    Number.isFinite(value.lng)
  );
}

function readMapCenter(map, fallbackCenter = null) {
  const liveCenter = map?.getCenter?.();
  if (
    liveCenter &&
    typeof liveCenter.lat === "function" &&
    typeof liveCenter.lng === "function"
  ) {
    return { lat: liveCenter.lat(), lng: liveCenter.lng() };
  }
  return isLatLngLiteral(fallbackCenter) ? fallbackCenter : null;
}

function omitPostInstantiationMapOptions(options) {
  if (!options || typeof options !== "object") return options;
  const { renderingType, ...rest } = options;
  return rest;
}

export function recoverGoogleMapSurface(map, fallbackCenter = null) {
  const gm = window.google?.maps;
  if (!map || !gm?.event?.trigger) return false;

  const centerToRestore = readMapCenter(map, fallbackCenter);
  gm.event.trigger(map, "resize");

  if (centerToRestore) {
    if (typeof map.panTo === "function") {
      map.panTo(centerToRestore);
    } else if (typeof map.setCenter === "function") {
      map.setCenter(centerToRestore);
    }
  }

  return true;
}

const HomeTimeCapsuleMapV2 = memo(function HomeTimeCapsuleMapV2({
  center,
  userLocation,
  displayItems,
  directions,
  onLoad,
  onDrag,
  onDragEnd,
  onIdle,
  onMarkerClick,
  onClusterClick,
  onLongPress,
  onPoiClick,
  mapOptions,
  containerStyle,
}) {
  const theme = useTheme();
  const auth = getAuth();
  const currentUser = auth?.currentUser || null;
  const userProfileImage = useUserProfileStore(selectUserProfileImage);
  const userDisplayName = useUserProfileStore(selectUserDisplayName);
  const mapInstanceRef = useRef(null);
  const markerInstancesRef = useRef(new Map());
  const markerClickHandlerRef = useRef(onMarkerClick);
  const clusterClickHandlerRef = useRef(onClusterClick);
  const longPressTimerRef = useRef(null);
  const markerIconCacheRef = useRef(new Map());
  const clusterIconCacheRef = useRef(new Map());
  const zoomListenerRef = useRef(null);
  const mapRecoveryTimersRef = useRef([]);
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const [mapZoom, setMapZoom] = useState(DEFAULT_MAP_ZOOM);
  const [hasMapLoaded, setHasMapLoaded] = useState(false);

  const mergedMapOptions = useMemo(() => {
    const nextOptions = {
      clickableIcons: true,
      ...mapOptions,
    };
    return hasMapLoaded ? omitPostInstantiationMapOptions(nextOptions) : nextOptions;
  }, [hasMapLoaded, mapOptions]);

  const handleMapClick = useCallback((e) => {
    if (e && e.placeId) {
      if (typeof e.stop === "function") e.stop();
      onPoiClick?.({
        placeId: e.placeId,
        lat: e?.latLng?.lat?.(),
        lng: e?.latLng?.lng?.(),
      });
    }
  }, [onPoiClick]);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const clearMapRecoveryTimers = useCallback(() => {
    mapRecoveryTimersRef.current.forEach((timerId) => clearTimeout(timerId));
    mapRecoveryTimersRef.current = [];
  }, []);

  const scheduleMapRecovery = useCallback((map, fallbackCenter = null) => {
    if (!map) return;
    clearMapRecoveryTimers();
    mapRecoveryTimersRef.current = IOS_MAP_RECOVERY_TIMEOUTS_MS.map((delayMs) =>
      setTimeout(() => {
        recoverGoogleMapSurface(map, fallbackCenter);
      }, delayMs)
    );
  }, [clearMapRecoveryTimers]);

  const startLongPressTimer = useCallback((e) => {
    if (!onLongPress) return;
    clearLongPressTimer();
    const latLng = e?.latLng;
    if (!latLng) return;
    longPressTimerRef.current = setTimeout(() => {
      onLongPress({
        lat: latLng.lat(),
        lng: latLng.lng(),
        placeId: e?.placeId || "",
      });
    }, 550);
  }, [clearLongPressTimer, onLongPress]);

  const profileImageUrl = useMemo(
    () => String(userProfileImage || currentUser?.photoURL || "").trim(),
    [currentUser?.photoURL, userProfileImage]
  );
  const mapAvatarAttrs = useMemo(
    () => buildAvatarImageAttrs(profileImageUrl, { size: 40 }),
    [profileImageUrl]
  );

  useEffect(() => {
    setAvatarLoadFailed(false);
  }, [profileImageUrl]);

  const avatarFallbackLetter = useMemo(() => {
    const label = String(
      userDisplayName ||
      currentUser?.displayName ||
      currentUser?.email ||
      ""
    ).trim();
    return label ? label[0].toUpperCase() : "";
  }, [currentUser?.displayName, currentUser?.email, userDisplayName]);

  const userCircle = useMemo(
    () =>
      userLocation ? (
        <Circle
          key="user-location-circle-v2"
          center={userLocation}
          radius={1000}
          options={{
            fillColor: theme.colors.blue,
            fillOpacity: 0.1,
            strokeColor: theme.colors.blue,
            strokeOpacity: 0.4,
            strokeWeight: 1,
            clickable: false,
            zIndex: 1,
          }}
        />
      ) : null,
    [theme.colors.blue, userLocation]
  );

  const directionsOptions = useMemo(
    () => ({
      preserveViewport: true,
      suppressMarkers: true,
      polylineOptions: {
        strokeColor:
          theme.palette.mode === "dark" ? theme.colors.green : theme.colors.blue,
        strokeOpacity: theme.palette.mode === "dark" ? 0.95 : 0.85,
        strokeWeight: theme.palette.mode === "dark" ? 6 : 5,
        zIndex: 20,
      },
    }),
    [theme.colors.blue, theme.colors.green, theme.palette.mode]
  );

  useEffect(() => {
    return () => {
      clearLongPressTimer();
      clearMapRecoveryTimers();
    };
  }, [clearLongPressTimer, clearMapRecoveryTimers]);

  const avatarContainerStyle = useMemo(
    () => ({
      width: 40,
      height: 40,
      border: `3px solid ${theme.colors.white}`,
      borderRadius: "50%",
      overflow: "hidden",
      boxShadow: `0 0 4px ${alpha(theme.palette.common.black, 0.3)}`,
      transform: "translate(-50%, -50%)",
      backgroundColor: theme.colors.blue,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }),
    [theme.colors.blue, theme.colors.white, theme.palette.common.black]
  );

  const handleMapDrag = useCallback((e) => {
    if (DEBUG_HOME_MAP_SHOVEL) {
      console.log("[home-map-shovel] v2 onDrag", { at: Date.now() });
    }
    clearLongPressTimer();
    onDrag?.(e);
  }, [clearLongPressTimer, onDrag]);

  const handleMapDragEnd = useCallback((e) => {
    if (DEBUG_HOME_MAP_SHOVEL) {
      console.log("[home-map-shovel] v2 onDragEnd", { at: Date.now() });
    }
    clearLongPressTimer();
    onDragEnd?.(e);
  }, [clearLongPressTimer, onDragEnd]);

  const handleGoogleMapClick = useCallback((e) => {
    clearLongPressTimer();
    handleMapClick(e);
  }, [clearLongPressTimer, handleMapClick]);

  const handleMapLoad = useCallback((map) => {
    mapInstanceRef.current = map;
    setHasMapLoaded(true);
    setMapZoom(Number(map?.getZoom?.()) || DEFAULT_MAP_ZOOM);
    try {
      zoomListenerRef.current?.remove?.();
    } catch {}
    zoomListenerRef.current = map?.addListener?.("zoom_changed", () => {
      const nextZoom = Number(map?.getZoom?.()) || DEFAULT_MAP_ZOOM;
      setMapZoom((prev) => (prev === nextZoom ? prev : nextZoom));
    });
    scheduleMapRecovery(map, center);
    onLoad?.(map);
  }, [center, onLoad, scheduleMapRecovery]);

  const handleMapUnmount = useCallback(() => {
    mapInstanceRef.current = null;
    setHasMapLoaded(false);
    clearMapRecoveryTimers();
    try {
      zoomListenerRef.current?.remove?.();
    } catch {}
    zoomListenerRef.current = null;
    const markerById = markerInstancesRef.current;
    markerById.forEach((marker) => {
      try {
        marker.__clickListener?.remove?.();
      } catch {}
      marker.setMap(null);
    });
    markerById.clear();
  }, [clearMapRecoveryTimers]);

  const getMarkerIcon = useCallback((marker) => {
    const cacheKey = `${marker.id}:${marker.icon}:${marker.width}:${marker.height}`;
    const cached = markerIconCacheRef.current.get(cacheKey);
    if (cached) return cached;

    const nextIcon = {
      url: marker.icon,
      scaledSize: new window.google.maps.Size(marker.width, marker.height),
      anchor: new window.google.maps.Point(marker.width / 2, marker.height),
    };
    if (
      debugSponsoredAvatar &&
      !marker?.isDraft &&
      !marker?.isSent &&
      String(marker?.capsuleType || "").toLowerCase() === "sponsored"
    ) {
      console.log("[sponsored-avatar] map icon applied", {
        markerId: marker?.id,
        capsuleType: marker?.capsuleType,
        iconUrl: marker?.icon,
        senderId: marker?.senderId,
        businessId: marker?.businessId,
      });
    }
    markerIconCacheRef.current.set(cacheKey, nextIcon);
    return nextIcon;
  }, []);

  const getClusterIcon = useCallback((displayScale = 1) => {
    const scale = Number.isFinite(Number(displayScale))
      ? Math.min(1.3, Math.max(0.65, Number(displayScale)))
      : 1;
    const size = Math.max(1, Math.round(90 * scale));
    const anchorX = size / 2;
    const anchorY = size * CLUSTER_CIRCLE_CENTER_Y_RATIO;
    const cacheKey = `cluster:${size}`;
    const cached = clusterIconCacheRef.current.get(cacheKey);
    if (cached) return cached;

    const icon = {
      url: IconCluster,
      scaledSize: new window.google.maps.Size(size, size),
      anchor: new window.google.maps.Point(anchorX, anchorY),
      labelOrigin: new window.google.maps.Point(anchorX, anchorY),
    };
    clusterIconCacheRef.current.set(cacheKey, icon);
    return icon;
  }, []);

  useEffect(() => {
    markerClickHandlerRef.current = onMarkerClick;
  }, [onMarkerClick]);

  useEffect(() => {
    clusterClickHandlerRef.current = onClusterClick;
  }, [onClusterClick]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return undefined;

    const runRecovery = () => {
      recoverGoogleMapSurface(map, center);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") return;
      runRecovery();
    };

    const handlePageShow = () => runRecovery();
    const handleFocus = () => runRecovery();
    const handleResize = () => runRecovery();

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    window.visualViewport?.addEventListener?.("resize", handleResize);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      window.visualViewport?.removeEventListener?.("resize", handleResize);
    };
  }, [center]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    const gm = window.google?.maps;
    if (!map || !gm) return;

    const markerById = markerInstancesRef.current;
    const nextIds = new Set();

    (Array.isArray(displayItems) ? displayItems : []).forEach((item) => {
      if (!item?.key || !item?.position) return;
      nextIds.add(item.key);
      let marker = markerById.get(item.key);
      if (!marker) {
        marker = new gm.Marker({
          map,
          position: item.position,
          icon:
            item.kind === "cluster"
              ? getClusterIcon(item.displayScale)
              : getMarkerIcon(item.marker),
          label:
            item.kind === "cluster"
              ? getClusterLabel(item.count, item.displayScale)
              : null,
          clickable: true,
          optimized: item.kind !== "cluster",
          zIndex: item.kind === "cluster" ? CLUSTER_Z_INDEX : MARKER_Z_INDEX,
        });
        marker.__data = item;
        marker.__clickListener = marker.addListener("click", () => {
          const nextData = marker.__data;
          if (!nextData) return;
          if (nextData.kind === "cluster") {
            clusterClickHandlerRef.current?.(nextData.markers || []);
            map.panTo(nextData.position);
            return;
          }
          markerClickHandlerRef.current?.(nextData.marker);
        });
        markerById.set(item.key, marker);
      } else {
        marker.__data = item;
        marker.setPosition(item.position);
        marker.setIcon(
          item.kind === "cluster"
            ? getClusterIcon(item.displayScale)
            : getMarkerIcon(item.marker)
        );
        marker.setLabel?.(
          item.kind === "cluster"
            ? getClusterLabel(item.count, item.displayScale)
            : null
        );
        marker.setZIndex?.(item.kind === "cluster" ? CLUSTER_Z_INDEX : MARKER_Z_INDEX);
        marker.setClickable?.(true);
        marker.setOptions?.({ optimized: item.kind !== "cluster" });
        marker.setMap(map);
      }
    });

    markerById.forEach((marker, id) => {
      if (nextIds.has(id)) return;
      try {
        marker.__clickListener?.remove?.();
      } catch {}
      marker.setMap(null);
      markerById.delete(id);
    });
  }, [displayItems, getClusterIcon, getMarkerIcon, mapZoom]);

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      defaultCenter={center || MAP_FALLBACK_CENTER}
      center={center}
      zoom={DEFAULT_MAP_ZOOM}
      onLoad={handleMapLoad}
      onUnmount={handleMapUnmount}
      onDrag={handleMapDrag}
      onDragEnd={handleMapDragEnd}
      onIdle={(event) => {
        if (DEBUG_HOME_MAP_SHOVEL) {
          console.log("[home-map-shovel] v2 onIdle", { at: Date.now() });
        }
        const nextZoom = Number(mapInstanceRef.current?.getZoom?.()) || DEFAULT_MAP_ZOOM;
        setMapZoom((prev) => (prev === nextZoom ? prev : nextZoom));
        onIdle?.(event);
      }}
      onClick={handleGoogleMapClick}
      onMouseDown={startLongPressTimer}
      onMouseUp={clearLongPressTimer}
      onMouseMove={clearLongPressTimer}
      onTouchStart={startLongPressTimer}
      onTouchEnd={clearLongPressTimer}
      options={mergedMapOptions}
    >
      {userLocation && mapZoom >= USER_AVATAR_MIN_VISIBLE_ZOOM && (
        <OverlayView
          position={userLocation}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <div style={avatarContainerStyle}>
            {profileImageUrl && !avatarLoadFailed ? (
              <img
                src={mapAvatarAttrs.src}
                srcSet={mapAvatarAttrs.srcSet || undefined}
                sizes={mapAvatarAttrs.sizes || undefined}
                alt="You"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                loading="lazy"
                decoding="async"
                onError={() => setAvatarLoadFailed(true)}
              />
            ) : avatarFallbackLetter ? (
              <span style={{ color: theme.palette.common.white, fontSize: 16, fontWeight: 700 }}>
                {avatarFallbackLetter}
              </span>
            ) : (
              <PersonIcon style={{ color: theme.palette.common.white, fontSize: 24 }} />
            )}
          </div>
        </OverlayView>
      )}

      {userCircle}

      {directions && (
        <DirectionsRenderer
          directions={directions}
          options={directionsOptions}
        />
      )}
    </GoogleMap>
  );
});

export default HomeTimeCapsuleMapV2;
